import { useQueries } from "@tanstack/react-query";
import { fetchSubstanceSupplyFlagsOne } from "@/services/substance_api";
import type { SubstanceSupplyFlags } from "@/utils/types/substance";

export const substanceSupplyFlagsKeys = {
  /**
   * One cache entry per (flavorId, substanceKey). Adding or removing a
   * compound from the flask only triggers a fetch for the new compound;
   * everything else stays served from cache. An older keying strategy
   * folded every substance in the flask into a single sorted-array key,
   * so any change to the formulation invalidated every row's flags — the
   * INFO column flashed back to its loading state on every autosave.
   */
  one: (flavorId: string | null, key: string) =>
    ["substanceSupplyFlags", flavorId, key] as const,
};

type SubstanceSupplyFlagsResult = {
  data: Record<string, SubstanceSupplyFlags>;
  isPending: boolean;
};

/**
 * Per-substance supply-flags resolver. Returns the same record shape as the
 * old batch hook so FlaskPanel doesn't need to change. Each substance is its
 * own React Query entry — adding a new substance fires one HTTP call;
 * unchanged substances stay served from cache (no flicker).
 *
 * Network amplification is avoided by going through `fetchSubstanceSupplyFlagsOne`,
 * which coalesces all calls made in the same microtask window into a single
 * batched POST. Initial flask load with N substances → 1 HTTP request;
 * subsequent add of one substance → 1 HTTP request for just that key.
 */
export function useSubstanceSupplyFlags(
  flavorId: string | null,
  substanceKeys: string[],
): SubstanceSupplyFlagsResult {
  const results = useQueries({
    queries: substanceKeys.map((key) => ({
      queryKey: substanceSupplyFlagsKeys.one(flavorId, key),
      queryFn: () => fetchSubstanceSupplyFlagsOne(key),
      enabled: flavorId !== null,
      staleTime: 30_000,
    })),
  });

  const data: Record<string, SubstanceSupplyFlags> = {};
  let isPending = false;
  for (let i = 0; i < substanceKeys.length; i++) {
    const r = results[i];
    if (!r) continue;
    if (r.isPending) isPending = true;
    if (r.data) data[substanceKeys[i]!] = r.data;
  }
  return { data, isPending };
}
